import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../api/client";
import { COLORS, RADIUS } from "../../theme";
import Card from "../../components/ui/Card";
import Btn from "../../components/ui/Btn";
import Input from "../../components/ui/Input";
import Badge from "../../components/ui/Badge";
import Modal from "../../components/ui/Modal";
import Avatar from "../../components/ui/Avatar";
import EstadoCarregando from "../../components/ui/EstadoCarregando";
import EstadoVazio from "../../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../../components/ui/ThFiltro";

const PERFIL_TONE = { admin: "primary", gestor: "success", funcionario: "neutral", admin_empresa: "warning" };
const PERFIL_LABEL = { admin: "Admin PontoZipar", admin_empresa: "Administrador", gestor: "Gestor", funcionario: "Funcionário" };
const PERFIS_EDITAVEIS = [
  { value: "admin_empresa", label: "Administrador" },
  { value: "gestor", label: "Gestor" },
  { value: "funcionario", label: "Funcionário" },
];

function selectStyle() {
  return { width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text };
}
function labelStyle() {
  return { fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 };
}

export default function AdminUsuarios() {
  const { token } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nome: "", email: "", perfil: "funcionario", senha: "" });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEmail, setFiltroEmail] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/admin/usuarios", undefined, token);
      setUsuarios(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const alternarAtivo = async (u) => {
    await apiFetch("PUT", `/api/admin/usuarios/${u.id}`, { ativo: !u.ativo }, token);
    carregar();
  };

  const abrirEdicao = (u) => {
    setEditando(u);
    setForm({ nome: u.nome, email: u.email, perfil: u.perfil, senha: "" });
    setErro("");
    setModalAberto(true);
  };

  const salvar = async () => {
    setErro("");
    if (!form.nome.trim() || !form.email.trim()) {
      setErro("Nome e e-mail são obrigatórios");
      return;
    }
    setSalvando(true);
    try {
      const payload = { nome: form.nome, email: form.email, perfil: form.perfil };
      if (form.senha) payload.senha = form.senha;
      await apiFetch("PUT", `/api/admin/usuarios/${editando.id}`, payload, token);
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const clientesDisponiveis = [...new Set(usuarios.map((u) => u.tenant_nome).filter(Boolean))].sort();
  const usuariosFiltrados = usuarios.filter((u) => {
    if (filtroNome && !u.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroEmail && !u.email.toLowerCase().includes(filtroEmail.toLowerCase())) return false;
    if (filtroCliente && u.tenant_nome !== filtroCliente) return false;
    if (filtroPerfil && u.perfil !== filtroPerfil) return false;
    if (filtroStatus && (filtroStatus === "ativo") !== !!u.ativo) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroNome || filtroEmail || filtroCliente || filtroPerfil || filtroStatus);
  const limparFiltros = () => {
    setFiltroNome(""); setFiltroEmail(""); setFiltroCliente(""); setFiltroPerfil(""); setFiltroStatus("");
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Usuários
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Todos os usuários de todos os clientes do PontoZipar.
        </p>
      </div>

      <Modal open={modalAberto} onClose={() => setModalAberto(false)} title={`Editar — ${editando?.nome || ""}`} width={440}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          <Input label="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {editando?.perfil !== "admin" && (
            <div>
              <label style={labelStyle()}>Perfil</label>
              <select value={form.perfil} onChange={(e) => setForm({ ...form, perfil: e.target.value })} style={selectStyle()}>
                {PERFIS_EDITAVEIS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          )}
          <Input
            label="Nova senha (deixe em branco pra manter a atual)"
            type="text"
            value={form.senha}
            onChange={(e) => setForm({ ...form, senha: e.target.value })}
            placeholder="Mínimo 6 caracteres"
          />
          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}
          <Btn onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar alterações"}</Btn>
        </div>
      </Modal>

      {usuarios.length > 0 && (
        <BarraResumoFiltro
          total={usuariosFiltrados.length}
          totalGeral={usuarios.length}
          unidade="usuário(s)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : usuarios.length === 0 ? (
          <EstadoVazio icon="users" texto="Nenhum usuário cadastrado ainda." />
        ) : usuariosFiltrados.length === 0 ? (
          <EstadoVazio icon="users" texto="Nenhum usuário encontrado com esse filtro." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <ThFiltroTexto label="Nome" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar..." minWidth={150} />
                  <ThFiltroTexto label="E-mail" value={filtroEmail} onChange={setFiltroEmail} placeholder="Buscar..." minWidth={170} />
                  <ThFiltroSelect
                    label="Cliente" value={filtroCliente} onChange={setFiltroCliente} minWidth={160}
                    opcoes={[{ value: "", label: "Todos" }, ...clientesDisponiveis.map((c) => ({ value: c, label: c }))]}
                  />
                  <ThFiltroSelect
                    label="Perfil" value={filtroPerfil} onChange={setFiltroPerfil} minWidth={150}
                    opcoes={[{ value: "", label: "Todos" }, ...Object.entries(PERFIL_LABEL).map(([value, label]) => ({ value, label }))]}
                  />
                  <ThFiltroSelect
                    label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={110}
                    opcoes={[{ value: "", label: "Todos" }, { value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]}
                  />
                  <ThSimples label="" />
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map((u) => (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar nome={u.nome} size={28} />
                        {u.nome}
                      </div>
                    </td>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{u.email}</td>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{u.tenant_nome || "—"}</td>
                    <td style={{ padding: "14px 20px" }}>
                      <Badge tone={PERFIL_TONE[u.perfil] || "neutral"}>{PERFIL_LABEL[u.perfil] || u.perfil}</Badge>
                    </td>
                    <td style={{ padding: "14px 20px" }}>
                      <Badge tone={u.ativo ? "success" : "neutral"}>{u.ativo ? "Ativo" : "Inativo"}</Badge>
                    </td>
                    <td style={{ padding: "14px 20px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {u.perfil !== "admin" && (
                        <button
                          onClick={() => abrirEdicao(u)}
                          style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.primary, cursor: "pointer", marginRight: 8 }}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        onClick={() => alternarAtivo(u)}
                        style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}
                      >
                        {u.ativo ? "Desativar" : "Ativar"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
